import { useEffect, useRef, useState } from "react";
import { api, formatarErroApi, API_BASE } from "@/api";
import { LayoutAutenticado } from "@/componentes/LayoutAutenticado";
import { useAutenticacao } from "@/contextos/AutenticacaoContexto";
import { toast } from "sonner";
import { X, Plus, UserCog, Upload, FileDown, Edit2 } from "lucide-react";

// Funções de formatação
const formatarCPF = (valor) => {
  const apenasNumeros = valor.replace(/\D/g, "");
  if (apenasNumeros.length <= 3) return apenasNumeros;
  if (apenasNumeros.length <= 6) return `${apenasNumeros.slice(0, 3)}.${apenasNumeros.slice(3)}`;
  if (apenasNumeros.length <= 9) return `${apenasNumeros.slice(0, 3)}.${apenasNumeros.slice(3, 6)}.${apenasNumeros.slice(6)}`;
  return `${apenasNumeros.slice(0, 3)}.${apenasNumeros.slice(3, 6)}.${apenasNumeros.slice(6, 9)}-${apenasNumeros.slice(9, 11)}`;
};

const formatarTelefone = (valor) => {
  const apenasNumeros = valor.replace(/\D/g, "");
  if (apenasNumeros.length <= 2) return apenasNumeros;
  if (apenasNumeros.length <= 7) return `(${apenasNumeros.slice(0, 2)}) ${apenasNumeros.slice(2)}`;
  return `(${apenasNumeros.slice(0, 2)}) ${apenasNumeros.slice(2, 7)}-${apenasNumeros.slice(7, 11)}`;
};

export default function GerenciarUsuarios() {
  const { usuario } = useAutenticacao();
  const ehSuper = usuario?.perfil === "SUPER_ADMIN";
  const [usuarios, setUsuarios] = useState([]);
  const [filtro, setFiltro] = useState("APOSTADOR");
  const [modal, setModal] = useState(null);
  const [modalCsv, setModalCsv] = useState(false);
  const [usuarioEdicao, setUsuarioEdicao] = useState(null);

  const carregar = async () => {
    const { data } = await api.get("/usuarios", { params: filtro ? { perfil: filtro } : {} });
    setUsuarios(data);
  };

  useEffect(() => { carregar(); }, [filtro]);


  const excluir = async (u) => {
    if (!confirm(`Excluir ${u.nomeCompleto}?`)) return;
    try {
      await api.delete(`/usuarios/${u.id}`);
      toast.success("Usuário excluído");
      carregar();
    } catch (e) { toast.error(formatarErroApi(e)); }
  };

  return (
    <LayoutAutenticado>
      <div data-testid="pagina-usuarios">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-amarelobrasil">Cadastros</div>
            <h1 className="font-heading text-4xl font-bold uppercase mt-1">Usuários</h1>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <select
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              className="surface-card px-4 py-2.5 rounded-md text-sm border border-white/10"
              data-testid="select-filtro-perfil"
            >
              <option value="">Todos</option>
              <option value="APOSTADOR">Apostadores</option>
              <option value="ADMINISTRADOR">Administradores</option>
              <option value="SUPER_ADMIN">Super Admins</option>
            </select>
            <button
              onClick={() => setModalCsv(true)}
              className="px-4 py-2.5 rounded-md text-xs font-heading uppercase tracking-widest border border-white/10 hover:bg-white/5 flex items-center gap-2"
              data-testid="btn-importar-csv"
            >
              <Upload className="w-4 h-4" /> Importar CSV
            </button>
            <button
              onClick={() => setModal({ tipo: "apostador" })}
              className="px-4 py-2.5 rounded-md text-xs font-heading uppercase tracking-widest bg-verdebrasil hover:bg-verdebrasil-dark flex items-center gap-2"
              data-testid="btn-novo-apostador"
            >
              <Plus className="w-4 h-4" /> Apostador
            </button>
            {ehSuper && (
              <button
                onClick={() => setModal({ tipo: "admin" })}
                className="px-4 py-2.5 rounded-md text-xs font-heading uppercase tracking-widest border border-amarelobrasil text-amarelobrasil hover:bg-amarelobrasil hover:text-superficie-base flex items-center gap-2"
                data-testid="btn-novo-admin"
              >
                <UserCog className="w-4 h-4" /> Admin
              </button>
            )}
          </div>
        </div>

        <div className="surface-card rounded-2xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-[10px] uppercase tracking-widest text-white/50 font-bold">
              <tr>
                <th className="px-4 py-3 text-left">Nome</th>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-left">Perfil</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {usuarios.map((u) => (
                <tr key={u.id} data-testid={`linha-usuario-${u.id}`}>
                  <td className="px-4 py-3 font-bold">{u.nomeCompleto}</td>
                  <td className="px-4 py-3 text-white/70">{u.email}</td>
                  <td className="px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-amarelobrasil">{u.perfil?.replace("_", " ")}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-[10px] uppercase tracking-widest font-bold ${u.status === "ATIVO" ? "bg-verdebrasil/20 text-verdebrasil" : "bg-red-500/20 text-red-400"}`}>{u.status}</span>
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    {u.perfil === "APOSTADOR" && (
                      <button
                        onClick={() => setUsuarioEdicao(u)}
                        className="text-xs font-bold uppercase tracking-widest text-white/70 hover:text-white inline-flex items-center gap-1"
                        data-testid={`btn-editar-${u.id}`}
                      >
                        <Edit2 className="w-3 h-3" /> Editar
                      </button>
                    )}
                    {ehSuper && u.id !== usuario.id && (
                      <button
                        onClick={() => excluir(u)}
                        className="text-xs font-bold uppercase tracking-widest text-red-400 hover:text-red-300"
                        data-testid={`btn-excluir-${u.id}`}
                      >
                        Excluir
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {usuarios.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-white/40">Nenhum usuário encontrado.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modal && <ModalNovoUsuario tipo={modal.tipo} onFechar={() => setModal(null)} onSalvo={() => { setModal(null); carregar(); }} />}
      {usuarioEdicao && <ModalEditarApostador usuario={usuarioEdicao} onFechar={() => setUsuarioEdicao(null)} onSalvo={() => { setUsuarioEdicao(null); carregar(); }} />}
      {modalCsv && <ModalImportarCsv onFechar={() => setModalCsv(false)} onConcluido={() => { setModalCsv(false); carregar(); }} />}
    </LayoutAutenticado>
  );
}

function ModalNovoUsuario({ tipo, onFechar, onSalvo }) {
  const [form, setForm] = useState({});
  const [enviando, setEnviando] = useState(false);
  const atualizar = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submeter = async (e) => {
    e.preventDefault();
    setEnviando(true);
    try {
      const dados = { ...form };
      if (tipo === "apostador") {
        dados.cpf = dados.cpf.replace(/\D/g, "");
        dados.telefone = dados.telefone.replace(/\D/g, "");
        await api.post("/usuarios/apostadores", dados);
      } else {
        dados.telefone = dados.telefone.replace(/\D/g, "");
        await api.post("/usuarios/administradores", { ...dados, perfil: dados.perfil || "ADMINISTRADOR" });
      }
      toast.success("Usuário criado");
      onSalvo();
    } catch (e) { toast.error(formatarErroApi(e)); }
    finally { setEnviando(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <form onSubmit={submeter} className="surface-card rounded-2xl p-8 max-w-lg w-full relative space-y-4" data-testid="modal-novo-usuario">
        <button type="button" onClick={onFechar} className="absolute top-4 right-4 text-white/60 hover:text-white"><X className="w-5 h-5"/></button>
        <h2 className="font-heading uppercase font-bold text-2xl">Novo {tipo === "apostador" ? "Apostador" : "Administrador"}</h2>

        <Input label="Nome completo" testId="m-nome" value={form.nomeCompleto || ""} onChange={(v) => atualizar("nomeCompleto", v)} required />
        {tipo === "apostador" && (
          <Input label="Apelido" testId="m-nomePublico" value={form.nomePublico || ""} onChange={(v) => atualizar("nomePublico", v)} />
        )}
        <Input label="Email" type="email" testId="m-email" value={form.email || ""} onChange={(v) => atualizar("email", v)} required />
        {tipo === "apostador" && (
          <>
            <Input label="CPF" testId="m-cpf" value={form.cpf || ""} onChange={(v) => atualizar("cpf", v)} format="cpf" required />
            <Input label="Data de nascimento" type="date" testId="m-dataNascimento" value={form.dataNascimento || ""} onChange={(v) => atualizar("dataNascimento", v)} required />
            <Input label="Telefone" testId="m-telefone" value={form.telefone || ""} onChange={(v) => atualizar("telefone", v)} format="telefone" />
          </>
        )}
        {tipo === "admin" && (
          <Input label="Telefone" testId="m-telefone" value={form.telefone || ""} onChange={(v) => atualizar("telefone", v)} />
        )}
        <Input label="Senha" type="password" testId="m-senha" value={form.senha || ""} onChange={(v) => atualizar("senha", v)} required />
        {tipo === "admin" && (
          <div>
            <label className="text-[10px] uppercase tracking-[0.3em] text-white/60 font-bold">Perfil</label>
            <select
              value={form.perfil || "ADMINISTRADOR"}
              onChange={(e) => atualizar("perfil", e.target.value)}
              className="mt-1 w-full bg-superficie-base border border-white/10 rounded-lg px-4 py-3 text-sm"
              data-testid="m-perfil"
            >
              <option value="ADMINISTRADOR">Administrador</option>
              <option value="SUPER_ADMIN">Super Admin</option>
            </select>
          </div>
        )}
        <button
          type="submit"
          disabled={enviando}
          className="w-full py-3 rounded-md font-heading uppercase tracking-widest text-sm bg-verdebrasil hover:bg-verdebrasil-dark disabled:opacity-50"
          data-testid="btn-salvar-usuario"
        >
          {enviando ? "Salvando..." : "Criar"}
        </button>
      </form>
    </div>
  );
}

function ModalEditarApostador({ usuario, onFechar, onSalvo }) {
  const [form, setForm] = useState({
    nomeCompleto: usuario.nomeCompleto || "",
    nomePublico: usuario.nomePublico || "",
    email: usuario.email || "",
    cpf: formatarCPF(usuario.cpf || ""),
    dataNascimento: usuario.dataNascimento || "",
    telefone: formatarTelefone(usuario.telefone || ""),
    bloqueado: usuario.status === "BLOQUEADO",
  });
  const [enviando, setEnviando] = useState(false);
  const atualizar = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submeter = async (e) => {
    e.preventDefault();
    setEnviando(true);
    try {
      await api.patch(`/usuarios/${usuario.id}`, {
        nomeCompleto: form.nomeCompleto,
        nomePublico: form.nomePublico,
        email: form.email,
        cpf: form.cpf.replace(/\D/g, ""),
        dataNascimento: form.dataNascimento,
        telefone: form.telefone.replace(/\D/g, ""),
        status: form.bloqueado ? "BLOQUEADO" : "ATIVO",
      });
      toast.success("Usuário atualizado");
      onSalvo();
    } catch (e) { toast.error(formatarErroApi(e)); }
    finally { setEnviando(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <form onSubmit={submeter} className="surface-card rounded-2xl p-8 max-w-lg w-full relative space-y-4" data-testid="modal-editar-apostador">
        <button type="button" onClick={onFechar} className="absolute top-4 right-4 text-white/60 hover:text-white"><X className="w-5 h-5"/></button>
        <h2 className="font-heading uppercase font-bold text-2xl">Editar Apostador</h2>

        <Input label="Nome completo" testId="e-nome" value={form.nomeCompleto} onChange={(v) => atualizar("nomeCompleto", v)} required />
        <Input label="Apelido" testId="e-nomePublico" value={form.nomePublico} onChange={(v) => atualizar("nomePublico", v)} />
        <Input label="Email" type="email" testId="e-email" value={form.email} onChange={(v) => atualizar("email", v)} required />
        <Input label="CPF" testId="e-cpf" value={form.cpf} onChange={(v) => atualizar("cpf", v)} format="cpf" required />
        <Input label="Data de nascimento" type="date" testId="e-dataNascimento" value={form.dataNascimento} onChange={(v) => atualizar("dataNascimento", v)} required />
        <Input label="Telefone" testId="e-telefone" value={form.telefone} onChange={(v) => atualizar("telefone", v)} format="telefone" />

        <div className="flex items-center gap-3 bg-white/5 rounded-lg p-4">
          <input
            type="checkbox"
            id="bloqueado"
            checked={form.bloqueado}
            onChange={(e) => atualizar("bloqueado", e.target.checked)}
            className="w-4 h-4 rounded cursor-pointer"
            data-testid="e-bloqueado"
          />
          <label htmlFor="bloqueado" className="text-sm font-bold cursor-pointer flex-1">
            Bloquear usuário
          </label>
        </div>

        <button
          type="submit"
          disabled={enviando}
          className="w-full py-3 rounded-md font-heading uppercase tracking-widest text-sm bg-verdebrasil hover:bg-verdebrasil-dark disabled:opacity-50"
          data-testid="btn-salvar-edicao"
        >
          {enviando ? "Salvando..." : "Salvar Alterações"}
        </button>
      </form>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required, testId, format }) {
  const handleChange = (e) => {
    let novoValor = e.target.value;
    
    if (format === "cpf") {
      novoValor = formatarCPF(novoValor);
    } else if (format === "telefone") {
      novoValor = formatarTelefone(novoValor);
    }
    
    onChange(novoValor);
  };

  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.3em] text-white/60 font-bold">{label}</label>
      <input
        type={type}
        value={value}
        onChange={handleChange}
        required={required}
        className="mt-1 w-full bg-superficie-base border border-white/10 rounded-lg px-4 py-3 text-sm focus:border-verdebrasil focus:outline-none focus:ring-1 focus:ring-verdebrasil"
        data-testid={testId}
      />
    </div>
  );
}


function ModalImportarCsv({ onFechar, onConcluido }) {
  const inputRef = useRef(null);
  const [arquivo, setArquivo] = useState(null);
  const [senhaPadrao, setSenhaPadrao] = useState("Bolao@2026");
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);

  const baixarModelo = () => {
    const conteudo =
      "nome,data de nascimento,cpf,telefone,email,nick\n" +
      "João da Silva,15/03/1990,123.456.789-09,(11) 99999-0001,joao@exemplo.com,Joãozinho\n" +
      "Maria Souza,02/12/1985,987.654.321-00,(11) 99999-0002,maria@exemplo.com,Mari\n";
    const blob = new Blob([conteudo], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "modelo_apostadores.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const submeter = async (e) => {
    e.preventDefault();
    if (!arquivo) {
      toast.error("Selecione um arquivo CSV");
      return;
    }
    setEnviando(true);
    setResultado(null);
    try {
      const form = new FormData();
      form.append("arquivo", arquivo);
      form.append("senhaPadrao", senhaPadrao);
      const token = localStorage.getItem("bolao_token");
      const resp = await fetch(`${API_BASE}/usuarios/apostadores/importar-csv`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.detail || "Falha na importação");
      setResultado(data);
      if (data.totalImportados > 0) {
        toast.success(`${data.totalImportados} apostador(es) importado(s)`);
      }
      if (data.totalFalhas > 0) {
        toast.warning(`${data.totalFalhas} linha(s) com falha`);
      }
    } catch (err) {
      toast.error(err.message || "Erro inesperado");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="surface-card rounded-2xl p-8 max-w-2xl w-full relative max-h-[90vh] overflow-y-auto" data-testid="modal-importar-csv">
        <button type="button" onClick={onFechar} className="absolute top-4 right-4 text-white/60 hover:text-white">
          <X className="w-5 h-5" />
        </button>
        <h2 className="font-heading uppercase font-bold text-2xl mb-2">Importar Apostadores via CSV</h2>
        <p className="text-sm text-white/60 mb-4">
          Colunas esperadas: <span className="font-mono text-amarelobrasil">nome, data de nascimento, cpf, telefone, email, nick</span>.
          A primeira linha do arquivo deve conter os cabeçalhos. Aceita separador <strong>vírgula</strong> ou <strong>ponto-e-vírgula</strong>.
        </p>

        <button
          type="button"
          onClick={baixarModelo}
          className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-amarelobrasil hover:text-white mb-6"
          data-testid="btn-baixar-modelo-csv"
        >
          <FileDown className="w-4 h-4" /> Baixar modelo CSV
        </button>

        <form onSubmit={submeter} className="space-y-4">
          <div
            onClick={() => inputRef.current?.click()}
            className="border-2 border-dashed border-white/10 hover:border-verdebrasil/60 rounded-xl p-8 text-center cursor-pointer transition-colors"
            data-testid="dropzone-csv"
          >
            <Upload className="w-8 h-8 mx-auto text-white/40 mb-3" />
            <div className="text-sm">
              {arquivo ? (
                <>
                  <span className="font-bold text-amarelobrasil">{arquivo.name}</span>
                  <div className="text-xs text-white/40 mt-1">{(arquivo.size / 1024).toFixed(1)} KB · clique para trocar</div>
                </>
              ) : (
                <>
                  <span className="font-bold">Clique para escolher o arquivo CSV</span>
                  <div className="text-xs text-white/40 mt-1">ou arraste-o até aqui</div>
                </>
              )}
            </div>
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setArquivo(e.target.files?.[0] || null)}
              className="hidden"
              data-testid="input-arquivo-csv"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.3em] text-white/60 font-bold">
              Senha padrão para todos os importados
            </label>
            <input
              type="text"
              value={senhaPadrao}
              onChange={(e) => setSenhaPadrao(e.target.value)}
              minLength={6}
              required
              className="mt-1 w-full bg-superficie-base border border-white/10 rounded-lg px-4 py-3 text-sm font-mono focus:border-verdebrasil focus:outline-none"
              data-testid="input-senha-padrao"
            />
            <div className="text-[10px] text-white/40 mt-1">
              Cada apostador receberá esta senha provisória. Eles poderão alterá-la após o primeiro login.
            </div>
          </div>

          <button
            type="submit"
            disabled={enviando || !arquivo}
            className="w-full py-3 rounded-md font-heading uppercase tracking-widest text-sm bg-verdebrasil hover:bg-verdebrasil-dark disabled:opacity-50 transition-all"
            data-testid="btn-enviar-csv"
          >
            {enviando ? "Importando..." : "Importar Apostadores"}
          </button>
        </form>

        {resultado && (
          <div className="mt-6 space-y-4" data-testid="resultado-importacao">
            <div className="grid grid-cols-2 gap-3">
              <div className="surface-elev rounded-lg p-4 text-center">
                <div className="font-heading font-black text-3xl text-verdebrasil tabular-nums">
                  {resultado.totalImportados}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-white/60 mt-1">Importados</div>
              </div>
              <div className="surface-elev rounded-lg p-4 text-center">
                <div className={`font-heading font-black text-3xl tabular-nums ${resultado.totalFalhas > 0 ? "text-red-400" : "text-white/40"}`}>
                  {resultado.totalFalhas}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-white/60 mt-1">Falhas</div>
              </div>
            </div>

            {resultado.totalFalhas > 0 && (
              <div className="surface-elev rounded-lg p-4 max-h-48 overflow-y-auto">
                <div className="text-xs font-bold uppercase tracking-widest text-red-400 mb-2">Linhas com erro</div>
                <ul className="text-xs space-y-1">
                  {resultado.falhas.map((f, idx) => (
                    <li key={idx} className="font-mono">
                      Linha <span className="text-amarelobrasil">{f.linha}</span>: {f.erro}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {resultado.totalImportados > 0 && (
              <button
                type="button"
                onClick={onConcluido}
                className="w-full py-3 rounded-md font-heading uppercase tracking-widest text-sm border border-amarelobrasil text-amarelobrasil hover:bg-amarelobrasil hover:text-superficie-base"
                data-testid="btn-concluir-importacao"
              >
                Fechar e atualizar lista
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
